import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Home, MapPin, PhoneCall } from "lucide-react";
import { bdt, bn, colorName, WC_PRODUCT_IDS, WC_VARIATION_IDS, WC_VARIATION_SKUS } from "@/lib/shop";
import { trackBrowserPurchase } from "@/lib/meta";

export default function OrderSuccess() {
    const order = useMemo(() => {
        try {
            return JSON.parse(sessionStorage.getItem("sm_order") || "null");
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (!order) return;
        const key = `sm_fired_${order.order_number}`;
        if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            const items = order.items || [];
            const contents = items.map((it) => ({
                id: String(WC_PRODUCT_IDS[it.color] || "khadi-punjabi"),
                quantity: it.qty,
                item_price: 900,
                variation_id: WC_VARIATION_IDS[it.color]?.[it.size] || null,
                sku: WC_VARIATION_SKUS[it.color]?.[it.size] || null,
                color: colorName(it.color),
                size: it.size,
            }));
            const contentIds = [...new Set(contents.map((c) => c.id))];
            trackBrowserPurchase(order.event_id, order.total, order.total_qty || order.items.length, contents, contentIds);
        }
    }, [order]);

    if (!order) {
        return (
            <div className="min-h-screen bg-[#FBF9F5] flex flex-col items-center justify-center px-6 text-center">
                <p className="font-serif text-2xl font-bold text-[#1E1B18] mb-3">কোনো সাম্প্রতিক অর্ডার পাওয়া যায়নি</p>
                <p className="text-sm text-[#5C554D] mb-6">নতুন অর্ডার করতে হোমপেজে ফিরে যান।</p>
                <Link to="/" data-testid="success-home-link"
                    className="bg-[#A84320] text-[#FBF9F5] font-semibold px-6 py-3 rounded-full text-sm hover:bg-[#8C3417] transition-colors">
                    হোমপেজে ফিরে যান
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FBF9F5] py-10 md:py-16 px-4">
            <div className="max-w-2xl mx-auto">
                <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    className="flex justify-center mb-6"
                >
                    <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E4EFE8] border border-[#C6DCCF]">
                        <CheckCircle2 size={42} className="text-[#2D5A43]" data-testid="order-success-icon" />
                    </span>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.15 }}
                    className="text-center mb-8"
                >
                    <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-[#1E1B18]" data-testid="success-headline">
                        আপনার অর্ডার গ্রহণ করা হয়েছে!
                    </h1>
                    <p className="mt-3 text-base text-[#4A453F] leading-relaxed max-w-lg mx-auto">
                        আমাদের কাস্টমার কেয়ার প্রতিনিধি আপনার দেওয়া মোবাইল নম্বরে ফোন করে পণ্যের বিবরণ,
                        ডেলিভারি চার্জ ও মোট মূল্য নিশ্চিত করবেন।
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.28 }}
                    className="rounded-2xl bg-white border border-[#E4DDD3] overflow-hidden shadow-sm"
                    data-testid="order-confirmation-modal"
                >
                    <div className="bg-[#1A1715] px-6 py-4 flex flex-wrap items-center justify-between gap-2">
                                            <div>
                                                <p className="text-xs text-[#B9B0A3]">অর্ডার নম্বর</p>
                                                                    <p className="text-lg font-bold text-[#C49A45]" data-testid="success-order-number">
                                                                        {order.wc_number ? `#${order.wc_number}` : order.order_number}
                                                                    </p>
                                                                </div>
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C49A45] bg-[#C49A45]/15 border border-[#C49A45]/30 rounded-full px-3 py-1.5" data-testid="success-status">
                            <PhoneCall size={12} />
                            ফোনে নিশ্চিতকরণের অপেক্ষায়
                        </span>
                    </div>
                    <div className="p-6 space-y-5">
                        <div>
                            <p className="text-sm font-semibold text-[#1E1B18] mb-2" data-testid="success-offer-label">{order.offer_label}</p>
                            <ul className="space-y-1.5">
                                {order.items.map((it, i) => (
                                    <li key={i} className="flex justify-between text-sm" data-testid={`success-item-${i}`}>
                                        <span className="text-[#5C554D]">পাঞ্জাবি {bn(i + 1)}</span>
                                        <span className="text-[#1E1B18] font-medium">{colorName(it.color)} / সাইজ {it.size} × {bn(it.qty)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="border-t border-[#E4DDD3] pt-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-[#5C554D]">সাবটোটাল</span>
                                <span data-testid="success-subtotal">{bdt(order.subtotal)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[#5C554D]">ডেলিভারি চার্জ ({order.delivery_zone_label})</span>
                                <span data-testid="success-delivery">{order.delivery_charge === 0 ? "ফ্রি" : bdt(order.delivery_charge)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-[#E4DDD3]">
                                <span className="font-semibold text-[#1E1B18]">মোট পরিশোধযোগ্য</span>
                                <span className="text-xl font-extrabold text-[#9E3B1B]" data-testid="success-total">{bdt(order.total)}</span>
                            </div>
                            <p className="text-xs text-[#8A8278]">পেমেন্ট: ক্যাশ অন ডেলিভারি (COD)</p>
                        </div>
                        <div className="rounded-xl bg-[#FBF9F5] border border-[#E4DDD3] p-4">
                            <p className="flex items-center gap-2 text-sm font-semibold text-[#1E1B18] mb-1.5">
                                <MapPin size={14} className="text-[#A84320]" /> ডেলিভারি ঠিকানা
                            </p>
                            <p className="text-sm text-[#4A453F] leading-relaxed" data-testid="success-address">
                                {order.name} — {order.phone}
                                <br />
                                {order.address}, বাংলাদেশ
                            </p>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.45 }}
                    className="mt-8 text-center space-y-3"
                >
                    <Link to="/" data-testid="success-home-button"
                        className="inline-flex items-center gap-2 bg-[#1A1715] text-[#F4EFE6] font-semibold px-8 py-3.5 rounded-full text-sm hover:bg-[#A84320] transition-colors duration-200">
                        <Home size={15} />
                        হোমপেজে ফিরে যান
                    </Link>
                    <a href="tel:+8801960654102" data-testid="success-support-phone"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#A84320] hover:underline">
                        <PhoneCall size={14} />
                        কাস্টমার কেয়ার: +880 1960-654102
                    </a>
                </motion.div>
            </div>
        </div>
    );
}
